import { useParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import {
  type GraphQLDepartureChild,
  GraphQLDepartureExitType,
} from '@sola/schema/dotcom/supporters/schema.generated.ts';
import {
  findDepartureCommitment,
  getSponsorYears,
} from '@sola/shared-utils/supporters/departures.ts';
import {
  useDepartureSummary,
  useSponsorshipsAny,
} from '@sola/supporter-sdk/hooks/commitments.ts';

interface DepartureSpotlightCardData {
  childGlobalId: string;
  childImageUrl?: string | null;
  childName: string;
  gender?: string | null;
  sponsorYears?: number;
  titleKey: string;
}

type DepartureSummaryLoadingState = {
  isFetching: boolean;
  isLoading: boolean;
  isPending: boolean;
};

export const isDepartureSummaryLoading = ({
  isFetching,
  isLoading,
  isPending,
}: DepartureSummaryLoadingState): boolean =>
  isLoading || isFetching || isPending;

export const getChildName = (child: GraphQLDepartureChild): string =>
  (child.displayName ?? '').trim();

export const hasMultipleSponsorships = (sponsorshipCount: number): boolean =>
  sponsorshipCount > 1;

export const useDepartureSpotlightCard = () => {
  const params = useParams<{ globalId: string }>();
  const globalId = params?.globalId;
  const { t } = useTranslation(['my']);
  const departureSummary = useDepartureSummary();
  const sponsorshipsQuery = useSponsorshipsAny();

  const departure = departureSummary.departureSummary?.departures.find(
    ({ departedChild }) => departedChild.globalId === globalId,
  );
  const isLoading =
    isDepartureSummaryLoading(departureSummary) ||
    isDepartureSummaryLoading(sponsorshipsQuery);

  const titleKey =
    departure?.exitType === GraphQLDepartureExitType.Unplanned
      ? 'my:sponsorshipRelationship.departures.spotlight.departure.unplannedTitle'
      : 'my:sponsorshipRelationship.departures.spotlight.departure.title';
  const commitment = departure
    ? findDepartureCommitment(
        sponsorshipsQuery.sponsorships,
        departure.departedChild,
      )
    : undefined;
  const sponsorYears = departure
    ? getSponsorYears(departure, sponsorshipsQuery.sponsorships)
    : undefined;
  const cardData: DepartureSpotlightCardData | undefined = departure
    ? {
        childGlobalId: departure.departedChild.globalId,
        childImageUrl: departure.departedChild.imageUrl,
        childName: getChildName(departure.departedChild),
        gender: commitment?.participant?.gender,
        ...(sponsorYears ? { sponsorYears } : {}),
        titleKey,
      }
    : undefined;

  return {
    t,
    hasGlobalId: !!globalId,
    hasMultipleSponsorships: hasMultipleSponsorships(
      sponsorshipsQuery.sponsorships.length,
    ),
    isLoading,
    hasError:
      departureSummary.isError ||
      sponsorshipsQuery.isError ||
      (!isLoading && !departure),
    cardData,
  };
};
